import { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  label: string;
  children: React.ReactElement;
  delay?: number;
  position?: 'top' | 'bottom';
}

export function Tooltip({ label, children, delay = 150, position = 'top' }: TooltipProps) {
  const [coords, setCoords] = useState<{ x: number; y: number } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const show = useCallback(() => {
    timerRef.current = setTimeout(() => {
      if (wrapperRef.current) {
        const rect = wrapperRef.current.getBoundingClientRect();
        setCoords({
          x: rect.left + rect.width / 2,
          y: position === 'top' ? rect.top - 6 : rect.bottom + 6,
        });
      }
    }, delay);
  }, [delay, position]);

  const hide = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setCoords(null);
  }, []);

  return (
    <div ref={wrapperRef} className="inline-flex shrink-0" onMouseEnter={show} onMouseLeave={hide}>
      {children}
      {coords && createPortal(
        <div
          style={{
            position: 'fixed',
            left: coords.x,
            top: coords.y,
            transform: position === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
            zIndex: 9999,
            pointerEvents: 'none',
          }}
        >
          <div className="bg-gray-900 text-gray-100 text-xs font-medium px-2 py-1 rounded shadow-lg whitespace-nowrap border border-white/10">
            {label}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
